import { createUseStyles } from "react-jss";
import {
    controlBackgroundColor,
    alpha,
    foregroundColor,
    darken,
    getSpacing,
} from "../../../theme/Mixins";
import * as ChoiceTokens from "../../../theme/choiceThemeTokens";
import { Theme } from "../../../types/Theme";
import {
    CAROUSEL_SCALE_CELL_HEIGHT_PX,
    CAROUSEL_SCALE_CELL_MAX_WIDTH_PX,
    CAROUSEL_SCALE_CELL_MIN_WIDTH_MOBILE_PX,
    CAROUSEL_SCALE_FLUID_MIN_WIDTH_LOWER_PX,
} from "../../../models/utilities/carrouselScaleLayoutUtilities";

const isModernTheme = (theme: Theme) => theme.UseModernTheme === true;

export const modernNpsCellBorderWidth = (_theme: Theme): number =>
    ChoiceTokens.MODERN_ACTIVE_BORDER_WIDTH_PX;

const modernNpsSelectedRingSpread = (theme: Theme): number =>
    Math.max(modernNpsCellBorderWidth(theme) - theme.BorderWidth, 1);

const modernNpsSelectedRingShadow = (theme: Theme): string =>
    `0 0 0 ${modernNpsSelectedRingSpread(theme)}px ${theme.PrimaryColor}`;

const resolveModernNpsFocusRingColor = (theme: Theme, selected?: boolean): string =>
    selected ? theme.PrimaryColor : (theme.NeutralDarkColor ?? theme.ForegroundColorDark);

/** @deprecated Use CAROUSEL_SCALE_CELL_MAX_WIDTH_PX from carrouselScaleLayoutUtilities. */
export const CAROUSEL_SCALE_CELL_MAX_SIZE_PX = CAROUSEL_SCALE_CELL_MAX_WIDTH_PX;

/** Min height (px) for stacked/mobile scale cells — WCAG minimum tap target. */
export const CAROUSEL_SCALE_CELL_MIN_SIZE_PX = CAROUSEL_SCALE_CELL_MIN_WIDTH_MOBILE_PX;

interface CarrouselScaleCellMinWidthProps {
    stackedLayout?: boolean;
    stretchRow?: boolean;
    minCellWidthPx?: number;
    cellWidth?: number;
}

interface CarrouselScaleItemStyleProps extends CarrouselScaleCellMinWidthProps {
    theme: Theme;
    selected?: boolean;
    cellHeight?: number;
    isMobileDimension?: boolean;
    isLargeDisplay?: boolean;
    contentPaddingX?: number;
}

function resolveCarrouselScaleCellMinWidth({
    stackedLayout,
    stretchRow,
    minCellWidthPx,
    cellWidth,
}: CarrouselScaleCellMinWidthProps): number | undefined {
    if (stackedLayout) {
        return undefined;
    }
    if (!stretchRow && cellWidth) {
        return cellWidth;
    }
    return minCellWidthPx ?? CAROUSEL_SCALE_FLUID_MIN_WIDTH_LOWER_PX;
}

const modernNpsFocusPseudoStyles = {
    "&:focus-visible": {
        boxShadow: ({ theme, selected }: { theme: Theme; selected?: boolean }) => {
            if (!isModernTheme(theme)) {
                return undefined;
            }
            const gap = theme.WhiteColor ?? theme.ControlBackground;
            const ring = resolveModernNpsFocusRingColor(theme, selected);
            const selection = selected ? `${modernNpsSelectedRingShadow(theme)}, ` : "";
            return `${selection}0 0 0 2px ${gap}, 0 0 0 4px ${ring}`;
        },
    },
};

export default createUseStyles<string, CarrouselScaleItemStyleProps>({
    item: {
        composes: "carrousel-scale__item",
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        flex: ({ stackedLayout, stretchRow }: CarrouselScaleItemStyleProps) =>
            stackedLayout ? "0 0 auto" : stretchRow ? "1 1 0" : "0 0 auto",
        margin: 3,
        minWidth: resolveCarrouselScaleCellMinWidth,
        width: ({ stackedLayout, stretchRow, cellWidth }: CarrouselScaleItemStyleProps) =>
            stackedLayout || stretchRow ? undefined : cellWidth,
    },
    choice: {
        background: ({ theme }: { theme: Theme }) =>
            alpha(theme.ControlBackground, theme.ControlAlpha),
        border: ({ theme }: { theme: Theme }) => [
            [theme.BorderWidth, "solid", theme.ControlBorderColor],
        ],
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        color: ({ theme }: { theme: Theme }) =>
            foregroundColor(theme, controlBackgroundColor(theme)),
        composes: "choice radio-button",
        aspectRatio: ({ stackedLayout }: { stackedLayout?: boolean }) =>
            stackedLayout ? "auto" : undefined,
        cursor: "pointer",
        flex: ({ stretchRow }: CarrouselScaleItemStyleProps) => (stretchRow ? 1 : "0 0 auto"),
        height: ({ stackedLayout, cellHeight }: { stackedLayout?: boolean; cellHeight?: number }) =>
            stackedLayout ? "auto" : (cellHeight ?? CAROUSEL_SCALE_CELL_HEIGHT_PX),
        maxHeight: ({ stackedLayout, cellHeight }: { stackedLayout?: boolean; cellHeight?: number }) =>
            stackedLayout ? "none" : (cellHeight ?? CAROUSEL_SCALE_CELL_HEIGHT_PX),
        maxWidth: ({ stackedLayout, stretchRow, cellWidth }: CarrouselScaleItemStyleProps) =>
            stackedLayout || stretchRow ? "none" : cellWidth,
        minWidth: resolveCarrouselScaleCellMinWidth,
        minHeight: ({ stackedLayout, cellHeight }: { stackedLayout?: boolean; cellHeight?: number }) =>
            stackedLayout ? CAROUSEL_SCALE_CELL_MIN_SIZE_PX : (cellHeight ?? CAROUSEL_SCALE_CELL_HEIGHT_PX),
        outline: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? "2px solid transparent" : "none",
        position: "relative",
        textAlign: "center",
        transition: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? "background 200ms, border-color 200ms, color 200ms, box-shadow 200ms"
                : "background 200ms, border-color 200ms, color 200ms",
        boxSizing: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? ("border-box" as const) : undefined,
        width: ({ stackedLayout, stretchRow, cellWidth }: CarrouselScaleItemStyleProps) =>
            stackedLayout || stretchRow ? "100%" : cellWidth ?? "100%",
        "&:before": {
            content: "none",
            display: "none",
            height: 0,
            paddingTop: 0,
        },
    },
    choiceModernFocus: {
        ...modernNpsFocusPseudoStyles,
    },
    dynamicChoice: {
        aspectRatio: "auto",
        width: "100%",
        height: "auto",
        maxHeight: "none",
        maxWidth: "none",
        margin: ({ theme }: { theme: Theme }) => [
            [
                getSpacing(theme, theme.ChoiceSpacing, theme.QuestionSpacing?.WithinComponents) / 2,
                0,
            ],
        ],
        minHeight: CAROUSEL_SCALE_CELL_MIN_SIZE_PX,
        padding: [[14.5, 16]],

        "&:before": {
            display: "none",
            height: 0,
            paddingTop: 0,
        },
    },
    choiceSelected: {
        background: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? alpha(theme.ControlBackground, theme.ControlAlpha)
                : theme.PrimaryColor,
        borderColor: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? theme.PrimaryColor
                : darken(theme.PrimaryColor, theme.ActiveHighlightValue),
        boxShadow: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? modernNpsSelectedRingShadow(theme) : undefined,
        color: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? theme.PrimaryColor
                : foregroundColor(theme, theme.PrimaryColor),
        composes: "choice--selected",
    },
    choiceHover: {
        composes: "choice--hover",
        background: ({ theme }: { theme: Theme }) => {
            if (!isModernTheme(theme)) {
                return undefined;
            }
            return alpha(theme.NeutralDarkColor, 0.08);
        },
    },
    choiceActive: {
        composes: "choice--active",
        background: ({ theme, selected }: CarrouselScaleItemStyleProps) => {
            if (isModernTheme(theme)) {
                return undefined;
            }
            return selected
                ? darken(theme.PrimaryColor, theme.ActiveHighlightValue)
                : darken(theme.ControlBackground, theme.ActiveBackgroundValue);
        },
        color: ({ theme, selected }: CarrouselScaleItemStyleProps) => {
            if (isModernTheme(theme)) {
                return undefined;
            }
            return selected
                ? foregroundColor(theme, darken(theme.PrimaryColor, theme.ActiveHighlightValue))
                : foregroundColor(
                      theme,
                      darken(theme.ControlBackground, theme.ActiveBackgroundValue),
                  );
        },
    },
    choiceDisabled: {
        composes: "choice--disabled",
        background: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? alpha(theme.ControlBackground, theme.ControlAlpha)
                : undefined,
        borderColor: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? alpha(theme.NeutralDarkColor, 0.35) : undefined,
        boxShadow: ({ theme }: { theme: Theme }) => (isModernTheme(theme) ? "none" : undefined),
        color: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? alpha(theme.NeutralDarkColor, 0.35) : undefined,
        cursor: ({ theme }: { theme: Theme }) => (isModernTheme(theme) ? "default" : undefined),
        pointerEvents: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? ("none" as const) : undefined,
    },
    content: {
        alignItems: "center",
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        left: 0,
        overflow: "auto",
        position: "absolute",
        right: 0,
        top: 0,
        padding: ({ contentPaddingX }: CarrouselScaleItemStyleProps) =>
            contentPaddingX !== undefined ? [[0, contentPaddingX]] : [[0, 5]],
        "& span": {
            hyphens: "auto",
            overflowWrap: "anywhere",
            textAlign: "center",
            whiteSpace: ({ stackedLayout }: CarrouselScaleItemStyleProps) =>
                stackedLayout ? "normal" : "nowrap",
            wordBreak: "break-word",
        },
    },
    hiddenChoice: {
        display: "none",
    },
});
